import {CreateGuestWithMinDataPayload, GuestApi} from '../api';
import {
  CreateGuestByParticipantPayload,
  CreateGuestPayload,
  GetGuestSchemaPayload,
  GuestSchemaResponse,
  LivenessDetection,
} from './types';
import {getHasSomeCustomFields, isBlobInstance, PAYLOAD_FIELDS_NAMES} from '../utils';
import {FetcherSettings} from '../api/apiClient';
import {CustomFormService} from './CustomFormService';

type CreateGuestParams = {
  data: CreateGuestPayload;
  customFormId?: string;
};

class GuestService {
  private readonly guestApi: GuestApi;
  private readonly customFormService: CustomFormService;

  constructor() {
    this.guestApi = new GuestApi();
    this.customFormService = new CustomFormService();
  }

  getFileName(name: keyof CreateGuestPayload, payload: CreateGuestPayload): string {
    return `${name}-${PAYLOAD_FIELDS_NAMES[name]}.${(payload[name] as Blob)?.['type']}`;
  }

  transformPayloadToFormData(payload: CreateGuestPayload): FormData {
    const formData = new FormData();
    for (const name in payload) {
      const fieldName = name as keyof CreateGuestPayload;
      const field = payload?.[fieldName];
      const addEmptySignature = fieldName === PAYLOAD_FIELDS_NAMES.signature && !field;
      if (field || field === false) {
        const value = field.toString() || '';
        if (isBlobInstance(field)) {
          formData.append(fieldName, field, this.getFileName(fieldName, payload));
        } else {
          formData.set(fieldName, value);
        }
      }
      if (addEmptySignature) {
        formData.set(fieldName, '');
      }
    }

    return formData;
  }

  async getOne(id: string) {
    return this.guestApi.getOne(id);
  }

  async createOrUpdate(
    {data: {id, ...data}, customFormId}: CreateGuestParams,
    resolverSettings?: FetcherSettings,
  ) {
    const hasCustomFields = getHasSomeCustomFields(data) && customFormId;
    if (!data) throw new Error('The payload or reservation id is missing');
    const payloadFormData = this.transformPayloadToFormData(data);

    const guestResult = id
      ? await this.guestApi.patch(id, payloadFormData, resolverSettings)
      : await this.guestApi.create(payloadFormData, resolverSettings);

    if (!hasCustomFields) {
      return guestResult;
    }
    const guestId = guestResult?.id;

    if (customFormId && guestId) {
      await this.customFormService.saveToFields(
        customFormId,
        guestId,
        data,
        resolverSettings,
      );
    }

    return guestResult;
  }

  createWithMinData(
    payload: CreateGuestWithMinDataPayload,
    resolverSettings?: FetcherSettings,
  ) {
    return this.guestApi.createMin(payload, resolverSettings);
  }

  createGuestByParticipant(
    payload: CreateGuestByParticipantPayload,
    resolverSettings?: FetcherSettings,
  ) {
    return this.guestApi.createGuestByParticipant(payload, resolverSettings);
  }

  async delete(guestId: string, resolverSettings?: FetcherSettings) {
    if (!guestId) throw new Error('The guest id argument is not found');
    return await this.guestApi.delete(guestId, resolverSettings);
  }

  async getSchema(
    payload: GetGuestSchemaPayload,
    version = '',
    resolverSettings?: FetcherSettings,
  ): Promise<GuestSchemaResponse> {
    if (!payload?.reservation_id)
      throw new Error('The reservation id argument is not found');

    return await this.guestApi.getSchema(payload, version, resolverSettings);
  }

  async livenessDetection(
    payload: LivenessDetection,
    resolverSettings?: FetcherSettings,
  ) {
    return await this.guestApi.livenessDetection(payload, resolverSettings);
  }
}

export {GuestService};
